import {
	Directive,
	Input,
	TemplateRef,
	ViewContainerRef,
	ElementRef,
	AfterContentInit,
	AfterViewInit,
	EmbeddedViewRef,
	OnDestroy
} from "@angular/core";

/** Structural Directive
Attach this directive to any element and provide a default template
to automatically render the default template inside the element, if
there is no content within the element this is attached to.
It doesn't matter if the content inside is projected or not.
*/
@Directive({
	selector: "[rbcSlotDefault]"
})
export class SlotDefaultDirective
	implements AfterContentInit, AfterViewInit, OnDestroy {
	private hasView = false;
	private _userContent: ElementRef | TemplateRef<any>;

	constructor(
		private hostEl: ElementRef,
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef
	) {}

	// provide the element or template that must be shown by DEFAULT, if no user projected content exists
	// WITHIN the view container of the element this directive is attached to
	@Input("rbcSlotDefault") set userContent(
		ref: ElementRef | TemplateRef<any>
	) {
		this._userContent = ref;
		if (!this.hasView) {
			this.viewContainer.createEmbeddedView(this.templateRef);
			this.hasView = true;
		} else if (this.hasView) {
			this.viewContainer.clear();
			this.hasView = false;
		}
	}
	get userContent() {
		return this._userContent;
	}

	ngOnInit(): void {}

	ngAfterContentInit(): void {}

	ngAfterViewInit(): void {
		// user content can be an instance of ElementRef or TemplateRef. Based on this,
		// different rendering actions can be taken
		const res = this.checkEmbeddedView(this.viewContainer);
		// no projected content
		if (!res && this.userContent instanceof TemplateRef) {
			this.viewContainer.clear();
			this.viewContainer.createEmbeddedView(this.userContent);
		}
	}

	ngOnDestroy(): void {
		this.viewContainer.clear();
		this.hasView = false;
	}

	/**
	 * checks if a view container contains an embedded view in the first level
	 * (no need to check recursively since any view on the root level must exist for
	 * deeper levels & root level automatically indicates projected content)
	 * @param viewContainer provide a reference to a view container
	 * @see https://angular.io/api/core/ViewContainerRef
	 */
	checkEmbeddedView(viewContainer: ViewContainerRef) {
		if (this.viewContainer.length) {
			for (
				let i = 0;
				i < (this.viewContainer as any)._embeddedViews.length;
				i++
			) {
				const eView = (this.viewContainer as any)._embeddedViews[i];
				if (eView.nodes.length) {
					for (let j = 0; j < eView.nodes.length; j++) {
						const node = eView.nodes[j];
						// look for renderElement or instance
						// if instance, need to traverse more
						if (this.checkIfValidNode(node)) {
							return true;
						}
					}
				}
			}
		}
		return false;
	}

	/**
	 * Instances are anything but actual DOM elements. They are directives. (components, structural etc.)
	 * @param instance provide an instance of an Angular object
	 */
	checkInstance(instance) {
		if (
			instance.hasOwnProperty("_viewRef") &&
			instance._viewRef &&
			"rootNodes" in instance._viewRef
		) {
			for (let i = 0; i < instance._viewRef.rootNodes.length; i++) {
				const res = this.checkIfValidRootNode(
					instance._viewRef.rootNodes[i]
				);
				if (res) {
					return true;
				}
			}
		} else if (instance.hasOwnProperty("_viewContainer")) {
			return this.checkEmbeddedView(instance._viewContainer);
		}
		return false;
	}

	/**
	 * Anything but a comment is a valid root node
	 * @param rootNode reference to an HTML Node
	 */
	checkIfValidRootNode(rootNode): boolean {
		return rootNode && "nodeType" in rootNode
			? rootNode.nodeType !== 8
			: false;
	}

	/**
	 * Checks if a node is an element or an instance of an Angular view object
	 * @param node reference to an Angular element node
	 */
	checkIfValidNode(node): boolean {
		if (node && node.hasOwnProperty("renderElement")) {
			return this.checkIfValidRootNode(node.renderElement);
		} else if (node && node.hasOwnProperty("instance")) {
			return this.checkInstance(node.instance);
		} else {
			return false;
		}
	}
}
